/* eslint-disable react-hooks/exhaustive-deps*/
import { FC, useEffect, useState } from 'react'

export interface IScrollTrackingOptions {
  elementId?: string
  threshold?: number
}

export interface IScrollTrackingData {
  isScrolled: boolean
}

export const useScrollTracking = (options?: IScrollTrackingOptions): IScrollTrackingData => {
  const { elementId, threshold } = options || {}
  const [isScrolled, setIsScrolled] = useState<boolean>(false)

  // enable element mode only when elementId provided (threshold optional)
  const useElementMode = !!elementId
  const thresholdNumber = typeof threshold === 'number' ? threshold : 0

  useEffect(() => {
    if (typeof window === 'undefined') return

    const check = () => {
      if (useElementMode) {
        const el = document.getElementById(elementId!)
        if (!el) {
          setIsScrolled(false)
          return
        }
        // compute element top relative to the whole document (robust when other elements exist above)
        const rect = el.getBoundingClientRect()
        const elementTopDocument = rect.top + window.scrollY
        // active only when the page scroll has reached (elementTop - threshold)
        const nearTop = window.scrollY >= elementTopDocument - thresholdNumber
        setIsScrolled((prev) => (prev === nearTop ? prev : nearTop))
      } else {
        // legacy/simple mode: active when page scrolled at all
        const scrolled = window.scrollY > 0
        setIsScrolled((prev) => (prev === scrolled ? prev : scrolled))
      }
    }

    // initial check
    check()

    window.addEventListener('scroll', check)
    window.addEventListener('resize', check)
    return () => {
      window.removeEventListener('scroll', check)
      window.removeEventListener('resize', check)
    }
  }, [elementId, thresholdNumber, useElementMode])

  return { isScrolled }
}

export interface IScrollTrackingProps extends IScrollTrackingOptions {
  onChange: (data: IScrollTrackingData) => void
}

const ScrollTracking: FC<IScrollTrackingProps> = (props) => {
  const { isScrolled } = useScrollTracking({
    elementId: props.elementId,
    threshold: props.threshold
  })

  useEffect(() => {
    props.onChange({ isScrolled })
  }, [isScrolled, props.onChange])

  return <></>
}

export default ScrollTracking
